import {Card, Intent} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {observer} from "mobx-react";
import React, {ChangeEvent, useMemo} from "react";

import {formatToUtc} from "../../../utils/format-to-utc";
import {
    AddButton,
    EditIconButton,
    NarrowDownButton,
    RefreshButton,
    TrashIconButton,
} from "../../atoms/buttons/buttons";
import LableInput from "../../atoms/inputs/lable-input";
import {
    DisplayFlexSpaceBetween,
    Div,
    FlexGapContainer,
    PageContainer,
} from "../../atoms/layouts/category-layout-containers";
import {Td, Tr} from "../../atoms/tables/table";
import PagerSummary from "../../atoms/typographies/pagination-summary";
import {Pager} from "../../molecules/pager/pager";
import {AdminBreadcrumb} from "../../organisms/admin-breadcrumb/admin-breadcrumb";
import {DeleteDialog} from "../../organisms/delete-dialog/delete-dialog";
import {TableLayout} from "../../organisms/table-layout/table-layout";
import {AppLayout} from "../../templates/app-layout/app-layout";
import {DatabaseTemplateListPageStore} from "./database-template-list-store";

const ValidationMessage = styled.div`
    margin-top: 4px;
    color: #c23030;
    font-size: 12px;
`;

// ヘッダー用のハンドラ型
type HeaderHandlers = {
    onSearchChange: (v: string) => void;
    onSearch: () => void;
    onClear: () => void;
    onPrevious: () => void;
    onNext: () => void;
};
// Table用のハンドラ型
type TableHandlers = {
    onRowClick: (id: string) => void;
    onEditClick: (id: string) => void;
    onDeleteClick: (id: string, title: string) => void;
};

type Props = {pageStore: DatabaseTemplateListPageStore};

// 1) 検索・ページャー部分を observer
const TemplateListHeader = observer(
    ({store, handlers}: {store: DatabaseTemplateListPageStore; handlers: HeaderHandlers}) => {
        const titleIntent = store.searchTitleErrorMessage ? Intent.DANGER : Intent.NONE;
        return (
            <Card>
                <DisplayFlexSpaceBetween>
                    <FlexGapContainer sx={{alignItems: "center"}}>
                        <Div sx={{marginTop: "-5px"}}>
                            <LableInput
                                id="search"
                                label="タイトル"
                                value={store.searchQuery ?? ""}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    handlers.onSearchChange(e.target.value)
                                }
                                intent={titleIntent}
                            />
                            {store.searchTitleErrorMessage ? (
                                <ValidationMessage data-testid="templateTitleErrorMessage">
                                    {store.searchTitleErrorMessage}
                                </ValidationMessage>
                            ) : null}
                        </Div>
                        <NarrowDownButton
                            onClick={handlers.onSearch}
                            disabled={!store.canSearch}
                            data-testid="searchButton"
                        >
                            絞り込み
                        </NarrowDownButton>
                        <RefreshButton
                            onClick={handlers.onClear}
                            disabled={!store.hasTitleParam}
                            data-testid="clearButton"
                        >
                            絞り込みクリア
                        </RefreshButton>
                    </FlexGapContainer>
                    <FlexGapContainer sx={{alignItems: "center"}}>
                        <Div sx={{marginTop: "10px"}}>
                            <PagerSummary
                                totalCount={store.templateList.totalCount}
                                startIndex={store.startIndex}
                                endIndex={store.endIndex}
                            />
                        </Div>
                        <Pager
                            previousExists={store.templateList.previousPageExists}
                            nextExists={store.templateList.nextPageExists}
                            onPreviousClicked={handlers.onPrevious}
                            onNextClicked={handlers.onNext}
                        />
                    </FlexGapContainer>
                </DisplayFlexSpaceBetween>
            </Card>
        );
    },
);

// 2) テーブル行部を observer
const TemplateListTable = observer(
    ({store, handlers}: {store: DatabaseTemplateListPageStore; handlers: TableHandlers}) => (
        <TableLayout areaLabel="テンプレート一覧" columns={store.DEFAULT_COLUMN_DEFS}>
            {store.templateList.templates &&
                store.templateList.templates.map((template) => (
                    <Tr
                        key={template.id}
                        style={{cursor: "pointer"}}
                        onClick={() => handlers.onRowClick(template.id)}
                        data-testid="templateRow"
                    >
                        <Td>{template.title}</Td>
                        <Td>{template.note}</Td>
                        <Td>{template.createdUserName}</Td>
                        <Td>{formatToUtc(template.createdAt)}</Td>
                        <Td>
                            <EditIconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlers.onEditClick(template.id);
                                }}
                                data-testid="editButton"
                            />
                            <TrashIconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlers.onDeleteClick(template.id, template.title);
                                }}
                                data-testid="deleteButton"
                            />
                        </Td>
                    </Tr>
                ))}
        </TableLayout>
    ),
);

//  3) ページ本体は observer を外し、必要な部分だけ再レンダーさせる
const DatabaseTemplateListPage: React.FC<Props> = ({pageStore}) => {
    const headerHandlers = useMemo<HeaderHandlers>(
        () => ({
            onSearchChange: pageStore.setSearch,
            onSearch: pageStore.searchAndLoad,
            onClear: pageStore.clearSearch,
            onPrevious: () => pageStore.changePage(pageStore.page - 1),
            onNext: () => pageStore.changePage(pageStore.page + 1),
        }),
        [pageStore],
    );
    const rowHandlers = useMemo<TableHandlers>(
        () => ({
            onRowClick: pageStore.toDetailPage,
            onEditClick: pageStore.toEditPage,
            onDeleteClick: pageStore.confirmDeleteTemplate,
        }),
        [pageStore],
    );

    return (
        <AppLayout
            breadcrumb={<AdminBreadcrumb factory={(b) => b.databaseTemplates().build()} />}
            buttons={
                <AddButton onClick={pageStore.toCreatePage} data-testid="addButton">
                    作成
                </AddButton>
            }
        >
            <PageContainer>
                <TemplateListHeader store={pageStore} handlers={headerHandlers} />
                <TemplateListTable store={pageStore} handlers={rowHandlers} />
            </PageContainer>
            <DeleteDialog />
        </AppLayout>
    );
};

export default DatabaseTemplateListPage;
